// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * match.ts — one round of Rootbound, plus everything the network needs from it.
 *
 * Deliberately knows NOTHING about Trystero, the DOM or the clock source: it
 * takes a `send` function and is driven by `tick(dt)`. That is what makes the
 * host-transfer contract testable without a network (see tests/takeover.test.ts),
 * and rhythm-relay shipped broken precisely because its co-op session had no
 * such seam and its `onHostChange` was never wired at all.
 *
 * The netcode is LOCKSTEP, not a snapshot star. Rootbound has no randomness in
 * play and no hidden information, so a turn is fully described by an Action and
 * both peers re-simulate it through `game.ts`. No board state ever crosses the
 * wire, which means:
 *   - there is structurally nothing to desync;
 *   - an illegal or hostile packet is REJECTED rather than absorbed;
 *   - and host transfer is a display concern, because the promoted peer already
 *     holds a byte-identical board. All the host actually owns is the CLOCK.
 */

import {
  type Action,
  applyAction,
  createGame,
  decodeAction,
  encodeAction,
  type GameState,
  isLegal,
  legalActions,
  other,
  resign,
  type Seat,
  type WireAction,
} from './game';
import { chooseAction } from './ai';
import type { Mode } from './modes';

/** How long a player has to move before the host plays a sensible move for them. */
export const TURN_MS = 60_000;
/**
 * How long the host waits after a peer vanishes before resigning their seat.
 *
 * Long enough that a two-second network blip is not a loss, short enough that
 * the survivor is never stranded on a board nobody can advance. Every peer must
 * reach the results screen (principle #9) — a frozen board is the failure mode
 * this exists to prevent.
 */
export const ABANDON_MS = 20_000;

export interface MatchDeps {
  /** Broadcast a move to the other peer. Solo passes a no-op. */
  send: (w: WireAction) => void;
  /** Called after every state change, for repainting. */
  onChange: (g: GameState, last: Action | null) => void;
  /** Called once, when the round ends. */
  onEnd: (g: GameState) => void;
}

export interface MatchOpts {
  mode: Mode;
  /** Which seat this peer plays, or null for a spectator. */
  selfSeat: Seat | null;
  isHost: boolean;
  /** Shared round seed — used for deterministic auto-moves, never for play. */
  seed: number;
  deps: MatchDeps;
}

export class Match {
  state: GameState;
  readonly selfSeat: Seat | null;
  private host: boolean;
  private readonly seed: number;
  private readonly deps: MatchDeps;
  private turnLeft = TURN_MS;
  private abandon: { seat: Seat; left: number } | null = null;
  private ended = false;

  constructor(o: MatchOpts) {
    this.state = createGame(o.mode);
    this.selfSeat = o.selfSeat;
    this.host = o.isHost;
    this.seed = o.seed;
    this.deps = o.deps;
  }

  get isHost(): boolean {
    return this.host;
  }

  /** Is it this peer's turn to act? */
  get myTurn(): boolean {
    return !this.state.over && this.selfSeat !== null && this.state.toMove === this.selfSeat;
  }

  get msLeft(): number {
    return Math.max(0, this.turnLeft);
  }

  /** Play a local action: apply it here, then tell the other peer. */
  play(a: Action): boolean {
    if (!this.myTurn || !isLegal(this.state, a)) return false;
    this.commit(a);
    this.deps.send(encodeAction(a));
    return true;
  }

  /**
   * Apply an action that arrived over the wire.
   *
   * Everything is validated: the packet must decode, it must be that seat's
   * turn, and the action must be in the legal list. A peer handed a bad packet
   * drops it and stays consistent — with lockstep that is the whole defence,
   * and it is why `applyAction` (which re-derives legality) is used here rather
   * than the fast unchecked path the AI uses.
   */
  receive(w: unknown, fromSeat: Seat): boolean {
    if (this.state.over) return false;
    const a = decodeAction(w);
    if (!a) return false;
    if (this.state.toMove !== fromSeat) return false;
    if (!isLegal(this.state, a)) return false;
    this.commit(a);
    return true;
  }

  private commit(a: Action): void {
    this.state = applyAction(this.state, a);
    this.turnLeft = TURN_MS;
    this.deps.onChange(this.state, a);
    if (this.state.over) this.finish();
  }

  private finish(): void {
    if (this.ended) return;
    this.ended = true;
    this.deps.onEnd(this.state);
  }

  /**
   * Promotion. The board needs no takeover — this peer has simulated every move
   * and its state is already byte-identical — so all that changes is that this
   * peer now runs the clock and the abandonment timer.
   */
  setHost(isHost: boolean): void {
    const gained = isHost && !this.host;
    this.host = isHost;
    if (gained) this.turnLeft = Math.max(this.turnLeft, TURN_MS / 2);
  }

  /** A peer disappeared. The host starts the countdown to resigning their seat. */
  peerLeft(seat: Seat): void {
    if (this.state.over || seat === this.selfSeat) return;
    this.abandon = { seat, left: ABANDON_MS };
  }

  /** They came back before the countdown expired. */
  peerReturned(): void {
    this.abandon = null;
  }

  get abandonMsLeft(): number | null {
    return this.abandon ? Math.max(0, this.abandon.left) : null;
  }

  /**
   * Drive the clock. Called from a `setInterval`, never from rAF alone — a
   * backgrounded tab stops firing rAF, which would freeze the authoritative
   * clock exactly when the host tabs away.
   */
  tick(dtMs: number): void {
    if (this.state.over) return;

    if (this.abandon) {
      this.abandon.left -= dtMs;
      if (this.abandon.left <= 0 && this.host) {
        const gone = this.abandon.seat;
        this.abandon = null;
        this.state = resign(this.state, gone);
        this.deps.onChange(this.state, null);
        this.finish();
        return;
      }
    }

    this.turnLeft -= dtMs;
    if (this.turnLeft > 0 || !this.host) return;

    // The clock ran out. Rather than stalling the room or punishing the player
    // with a loss, the host plays a reasonable move for them and broadcasts it
    // like any other. It goes over the wire as a normal action, so the peer
    // whose clock expired ends up in exactly the same position as everyone
    // else.
    const auto = this.autoMove();
    this.turnLeft = TURN_MS;
    this.commit(auto);
    this.deps.send(encodeAction(auto));
  }

  /** A deterministic, decent move — the same one on every peer. */
  autoMove(): Action {
    const moves = legalActions(this.state);
    if (moves.length === 0) return { k: 'pass' };
    return chooseAction(this.state, { strength: 1, seed: `${this.seed}:auto:${this.state.ply}` });
  }

  /** Local resignation (the "give up" button). */
  resignSelf(): void {
    if (this.state.over || this.selfSeat === null) return;
    this.state = resign(this.state, this.selfSeat);
    this.deps.onChange(this.state, null);
    this.finish();
  }

  /** Who won, from this peer's point of view. */
  outcome(): 'win' | 'loss' | 'draw' | 'none' {
    if (!this.state.over) return 'none';
    if (this.state.winner === null) return 'draw';
    if (this.selfSeat === null) return 'none';
    return this.state.winner === this.selfSeat ? 'win' : 'loss';
  }
}

/**
 * A one-shot timer that is safe to poke repeatedly.
 *
 * This exists because of a real, shipped-in-development hang. The solo bot was
 * scheduled with `clearTimeout(t); t = setTimeout(move, 420)` and poked by a
 * 300ms poll — so the poll cancelled and re-armed the timer roughly 120ms
 * before it would ever have fired, forever. The clock counted down, the board
 * never changed, and it read exactly like "the AI is thinking". Zero console
 * errors, and no unit test could see it because the whole thing lived in a
 * screen builder.
 *
 * Poking while a run is already pending is a NO-OP, which is the entire point.
 */
export function createOneShot(delayMs: number, run: () => void) {
  let pending = false;
  let id: ReturnType<typeof setTimeout> | null = null;
  return {
    poke(): void {
      if (pending) return;
      pending = true;
      id = setTimeout(() => {
        pending = false;
        id = null;
        run();
      }, delayMs);
    },
    cancel(): void {
      if (id !== null) clearTimeout(id);
      id = null;
      pending = false;
    },
    get isPending(): boolean {
      return pending;
    },
  };
}

/** Seat helper so both peers agree who is who, from the frozen roster. */
export function seatOf(roster: Array<{ id: string }>, peerId: string): Seat | null {
  const i = roster.findIndex((p) => p.id === peerId);
  if (i < 0 || i > 1) return null;
  return i as Seat;
}

export { other };
